import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { FOCUS_DISABLED_CLASS } from '../../focus-management';
import { ChevronUp, ChevronRight, ChevronDown } from '../../icons';
import { useIsInBottomSheet } from '../BottomSheet';
import { Card } from '../Card';
import { Checkbox } from '../Checkbox';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { useDevice } from '../Device';
import { Divider } from '../Divider';
import { IconButton } from '../IconButton';
import { useIntentBehavior } from '../IntentBehavior';
import { FullRow } from '../Layout';
import { useOverlayContext } from '../OverlayContext';
import { Typography } from '../Typography';
import styles from './TableList.module.scss';
import { actionColumnDef, expandIconStyle, useTableListContext } from './TableList.utils';
import { TableListItem } from './TableListItem';
let idCounter = 0;
const getID = ()=>{
    const id = (idCounter + 1) % (Number.MAX_SAFE_INTEGER - 1);
    idCounter = id;
    return id;
};
const TableListRowContext = /*#__PURE__*/ React.createContext({
    setForceHover () {},
    size: 'medium'
});
export const useTableListRowContext = ()=>useContext(TableListRowContext);
export const TableListRowTestIds = {
    ToggleExpand: (testid)=>"".concat(testid, "-toggle-expand"),
    MobileExpand: (testid)=>"".concat(testid, "-mobile-expand")
};
const isItemValuePresent = (value)=>value != null && value !== false && value !== '';
const MobileLabel = (param)=>{
    let { label } = param;
    const { text, hideColon } = label, typographyProps = _object_without_properties(label, [
        "text",
        "hideColon"
    ]);
    return /*#__PURE__*/ _jsxs(Typography, _object_spread_props(_object_spread({
        skin: "secondary"
    }, typographyProps), {
        className: styles.mobileCardRowLabel,
        children: [
            text,
            !hideColon && ':'
        ]
    }));
};
const TableListRow = /*#__PURE__*/ React.forwardRef(function TableListRow(props, ref) {
    const { as, className, items, expandedContent, expandedContentClassName, rowId, addPulse, onClick, size = 'medium', actionColumn, disableShadow, disableMarginBottom, intent, highlightOnHover, toggleExpandProps = {}, testId, mobile } = props, rest = _object_without_properties(props, [
        "as",
        "className",
        "items",
        "expandedContent",
        "expandedContentClassName",
        "rowId",
        "addPulse",
        "onClick",
        "size",
        "actionColumn",
        "disableShadow",
        "disableMarginBottom",
        "intent",
        "highlightOnHover",
        "toggleExpandProps",
        "testId",
        "mobile"
    ]);
    const { columnDefs, expandableRows, expandableRowsVariant = 'default', expandedRows, enableRowSelection, selectedRows, variant = 'default', hPadding, hasActionColumn, setSelectedRows, toggleExpandedRow, registerRow, unregisterRow, justifyBetween, disableMobile } = useTableListContext();
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const { pushBottomSheetPage } = useOverlayContext();
    const isMobileMode = (isMobileDevice || isInBottomSheet) && !disableMobile;
    const [rowEl, setRowEl] = useState(null);
    const expandedContentRef = useRef(null);
    const { getDisabled } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const [forceHover, setForceHover] = useState(false);
    const id = useMemo(()=>rowId !== undefined ? rowId : getID(), [
        rowId
    ]);
    const isExpanded = expandedRows[id] || false;
    const isSelected = selectedRows[id] || false;
    const RowElement = as || 'div';
    const contextValue = useMemo(()=>({
            setForceHover,
            size
        }), [
        size
    ]);
    const isExpandableDefaultVariant = expandableRows && expandableRowsVariant === 'default';
    const isExpandableClickableIconAndRowVariant = expandableRows && expandableRowsVariant === 'clickableIconAndRow';
    const isExpandableClickableIconVariant = expandableRows && (expandableRowsVariant === 'clickableIcon' || isExpandableClickableIconAndRowVariant);
    useEffect(()=>{
        if (toggleExpandProps.disabled) return;
        registerRow({
            rowId: id,
            isExpanded: isExpanded
        });
        return ()=>{
            unregisterRow(id);
        };
    }, [
        toggleExpandProps.disabled
    ]);
    const resizeObs = useMemo(()=>{
        return new ResizeObserver((entries)=>{
            if (!Array.isArray(entries) || !entries.length) return;
            for (const entry of entries){
                requestAnimationFrame(()=>{
                    if (!entry.target || !expandedContentRef.current) return;
                    expandedContentRef.current.style.width = "".concat(entry.target.scrollWidth, "px");
                });
            }
        });
    }, []);
    useEffect(()=>{
        if (rowEl && isExpanded) {
            resizeObs.observe(rowEl);
            return ()=>resizeObs.unobserve(rowEl);
        }
    }, [
        rowEl,
        resizeObs,
        isExpanded
    ]);
    const stickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return (_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky;
        }), [
        items,
        columnDefs
    ]);
    const stickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>col.sticky), [
        columnDefs
    ]);
    const nonStickyItems = useMemo(()=>items.filter((_, idx)=>{
            var _columnDefs_idx;
            return !((_columnDefs_idx = columnDefs[idx]) === null || _columnDefs_idx === void 0 ? void 0 : _columnDefs_idx.sticky);
        }), [
        items,
        columnDefs
    ]);
    const nonStickyColumnDefs = useMemo(()=>columnDefs.filter((col)=>!col.sticky), [
        columnDefs
    ]);
    if (isMobileMode) {
        const hasMobileExpandAction = expandableRows && !!expandedContent;
        const mobileItems = items.map((item, idx)=>({
                item,
                columnDef: columnDefs[idx]
            })).filter((param)=>{
            let { columnDef } = param;
            return (columnDef === null || columnDef === void 0 ? void 0 : columnDef.mobile) != null && !columnDef.mobile.hide;
        }).filter((param)=>{
            let { item, columnDef } = param;
            var _columnDef_mobile;
            return ((_columnDef_mobile = columnDef.mobile) === null || _columnDef_mobile === void 0 ? void 0 : _columnDef_mobile.showWhenEmpty) || isItemValuePresent(item.value);
        });
        return /*#__PURE__*/ _jsx(TableListRowContext.Provider, {
            value: contextValue,
            children: /*#__PURE__*/ _jsx(Card, {
                autoHeight: true,
                padding: "s",
                skin: mobile === null || mobile === void 0 ? void 0 : mobile.skin,
                borderRadius: "m",
                borderSkin: "default",
                testId: testId,
                className: cn(styles.mobileCard, className),
                disableSeparator: true,
                children: /*#__PURE__*/ _jsx("div", {
                    children: mobileItems.map((param, idx)=>{
                        let { item, columnDef } = param;
                        var _columnDef_mobile, _columnDef_mobile1, _columnDef_mobile_label, _columnDef_mobile2, _columnDef_mobile3;
                        return /*#__PURE__*/ _jsx(ConditionalWrapper, {
                            condition: idx === 0 && !!(hasActionColumn || hasMobileExpandAction),
                            wrapper: (children)=>/*#__PURE__*/ _jsxs(FullRow, {
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: styles.mobileRowBeforeActions,
                                            children: children
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: styles.mobileActions,
                                            children: [
                                                hasActionColumn && actionColumn,
                                                hasMobileExpandAction && /*#__PURE__*/ _jsx(IconButton, {
                                                    noPadding: true,
                                                    size: "small",
                                                    "data-testid": testId && TableListRowTestIds.MobileExpand(testId),
                                                    onClick: ()=>pushBottomSheetPage({
                                                            content: expandedContent()
                                                        }),
                                                    children: /*#__PURE__*/ _jsx(ChevronRight, {
                                                        size: 16
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                }),
                            children: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    ((_columnDef_mobile = columnDef.mobile) === null || _columnDef_mobile === void 0 ? void 0 : _columnDef_mobile.divider) && /*#__PURE__*/ _jsx("div", {
                                        "data-testid": testId && "".concat(testId, "-mobile-divider-").concat(idx),
                                        children: /*#__PURE__*/ _jsx(Divider, {
                                            margin: 4,
                                            skin: (mobile === null || mobile === void 0 ? void 0 : mobile.skin) === 'mobileGray' ? 'white' : undefined
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: cn(styles.mobileCardRow, {
                                            [styles.spaceBetween]: (_columnDef_mobile1 = columnDef.mobile) === null || _columnDef_mobile1 === void 0 ? void 0 : _columnDef_mobile1.spaceBetween
                                        }),
                                        "data-testid": testId && "".concat(testId, "-mobile-row-").concat(idx),
                                        children: [
                                            ((_columnDef_mobile2 = columnDef.mobile) === null || _columnDef_mobile2 === void 0 ? void 0 : (_columnDef_mobile_label = _columnDef_mobile2.label) === null || _columnDef_mobile_label === void 0 ? void 0 : _columnDef_mobile_label.text) && /*#__PURE__*/ _jsx(MobileLabel, {
                                                label: columnDef.mobile.label
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, _object_spread_props(_object_spread({
                                                skin: "secondary"
                                            }, (_columnDef_mobile3 = columnDef.mobile) === null || _columnDef_mobile3 === void 0 ? void 0 : _columnDef_mobile3.value), {
                                                className: styles.mobileCardRowValue,
                                                children: item.value
                                            }))
                                        ]
                                    })
                                ]
                            })
                        }, idx);
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs(TableListRowContext.Provider, {
        value: contextValue,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                ref: setRowEl,
                className: cn(styles.row, styles["".concat(variant, "RowVariant")], styles["".concat(size, "RowSize")], {
                    [styles.hover]: isExpanded && isExpandableDefaultVariant || forceHover,
                    [styles.selected]: isSelected,
                    [styles.pulse]: addPulse,
                    [styles.disableShadow]: disableShadow,
                    [styles.disableMarginBottom]: disableMarginBottom,
                    [styles.highlightOnHover]: highlightOnHover
                }),
                children: [
                    /*#__PURE__*/ _jsxs(RowElement, _object_spread_props(_object_spread({}, rest, testId != null ? {
                        'data-testid': testId
                    } : {}), {
                        ref: ref,
                        "data-id": id,
                        "data-checked": isSelected,
                        disabled: disabledFromIntent,
                        className: cn(styles.rowInner, styles["".concat(variant, "RowInnerVariant")], styles["".concat(size, "RowSize")], styles["".concat(hPadding, "Padding")], className, {
                            [styles.clickable]: onClick,
                            [styles.justifyBetween]: justifyBetween
                        }),
                        onClick: (event)=>{
                            (isExpandableDefaultVariant || isExpandableClickableIconAndRowVariant) && !toggleExpandProps.disabled && toggleExpandedRow(id);
                            onClick && onClick(event);
                        },
                        children: [
                            (enableRowSelection || isExpandableClickableIconVariant || stickyItems.length > 0) && /*#__PURE__*/ _jsxs("div", {
                                className: cn(styles.stickyCheckboxWrapper, styles["".concat(hPadding, "Padding")]),
                                children: [
                                    (enableRowSelection || isExpandableClickableIconVariant) && /*#__PURE__*/ _jsxs("div", {
                                        className: cn(styles.stickyCheckbox, styles["".concat(hPadding, "Padding")]),
                                        onClick: (e)=>e.stopPropagation(),
                                        children: [
                                            enableRowSelection && /*#__PURE__*/ _jsx(Checkbox, {
                                                testId: testId && "".concat(testId, "-checkbox"),
                                                checked: isSelected,
                                                onChange: (e)=>{
                                                    setSelectedRows({
                                                        [id]: e.target.checked
                                                    });
                                                    // This prevents TableListActions to be visible if we click and select a row.
                                                    if (document.body.classList.contains(FOCUS_DISABLED_CLASS)) {
                                                        e.target.blur();
                                                    }
                                                }
                                            }),
                                            isExpandableClickableIconVariant && /*#__PURE__*/ _jsx(IconButton, {
                                                size: "small",
                                                noPadding: true,
                                                testId: testId && TableListRowTestIds.ToggleExpand(testId),
                                                disabled: toggleExpandProps.disabled,
                                                tooltip: toggleExpandProps.tooltipText ? {
                                                    title: toggleExpandProps.tooltipText,
                                                    placement: 'right'
                                                } : undefined,
                                                onClick: ()=>toggleExpandedRow(id),
                                                children: isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {
                                                    size: 20
                                                }) : /*#__PURE__*/ _jsx(ChevronDown, {
                                                    size: 20
                                                })
                                            })
                                        ]
                                    }),
                                    stickyItems.length > 0 && /*#__PURE__*/ _jsx("div", {
                                        className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
                                        children: stickyItems.map((item, idx)=>/*#__PURE__*/ _jsx(TableListItem, {
                                                columnDef: stickyColumnDefs[idx],
                                                item: item
                                            }, idx))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.rowItemData, justifyBetween && styles.justifyBetween),
                                children: nonStickyItems.map((item, idx)=>/*#__PURE__*/ _jsx(TableListItem, {
                                        columnDef: nonStickyColumnDefs[idx],
                                        item: item
                                    }, idx))
                            }),
                            (hasActionColumn || isExpandableDefaultVariant) && /*#__PURE__*/ _jsxs("div", {
                                className: cn(styles.rowActionSticky, styles["".concat(hPadding, "Padding")]),
                                children: [
                                    hasActionColumn && /*#__PURE__*/ _jsx(TableListItem, {
                                        columnDef: actionColumnDef,
                                        item: {
                                            value: actionColumn || ''
                                        }
                                    }),
                                    isExpandableDefaultVariant && /*#__PURE__*/ _jsx("div", {
                                        style: expandIconStyle,
                                        children: isExpanded ? /*#__PURE__*/ _jsx(ChevronUp, {}) : /*#__PURE__*/ _jsx(ChevronRight, {})
                                    })
                                ]
                            })
                        ]
                    })),
                    isExpanded && isExpandableDefaultVariant && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.separator
                            }),
                            expandedContent && expandedContent()
                        ]
                    })
                ]
            }),
            isExpanded && isExpandableClickableIconVariant && expandedContent && /*#__PURE__*/ _jsx("div", {
                ref: expandedContentRef,
                className: cn(styles.expandedContent, expandedContentClassName),
                children: expandedContent()
            })
        ]
    });
});
TableListRow.displayName = 'TableListRow';
export { TableListRow };
